import React, { useId } from 'react';
import { advancedNumber } from '../../utils/advancedStatistics';
import './comparisonRadar.css';

export function comparisonRadarMetrics(keys, a, b) {
  return keys.flatMap(key => {
    const first = advancedNumber(a[key]), second = advancedNumber(b[key]);
    if (first === null || second === null) return [];
    const maximum = Math.max(first, second);
    return [{ key, a: maximum > 0 ? first / maximum : 0, b: maximum > 0 ? second / maximum : 0 }];
  });
}
const labelLines = label => label.split(' ').reduce((lines, word) => {
  const last = lines.length - 1;
  if (last >= 0 && `${lines[last]} ${word}`.length <= 12) lines[last] += ` ${word}`;
  else lines.push(word);
  return lines;
}, []);

export default function ComparisonRadar({ axes, title, description, emptyText, incompleteText, className = '' }) {
  const id = useId();
  const usable = axes.filter(axis => axis.values.every(value => advancedNumber(value) !== null && value >= 0));
  const metrics = comparisonRadarMetrics(usable.map(axis => axis.key),
    Object.fromEntries(usable.map(axis => [axis.key, axis.values[0]])),
    Object.fromEntries(usable.map(axis => [axis.key, axis.values[1]])));
  if (metrics.length < 3) return <p className={`comparison-radar-empty ${className}-empty`} role="status">{emptyText}</p>;
  const point = (index, radius) => {
    const angle = -Math.PI / 2 + index * 2 * Math.PI / metrics.length;
    return [210 + Math.cos(angle) * radius, 172 + Math.sin(angle) * radius];
  };
  const polygon = radius => metrics.map((_, index) => point(index, radius).join(',')).join(' ');
  return <figure className={`comparison-radar ${className}`}>
    <svg viewBox="0 0 420 350" role="img" aria-labelledby={`${id}-title ${id}-description`}>
      <title id={`${id}-title`}>{title}</title>
      <desc id={`${id}-description`}>{description}</desc>
      {[0.25, 0.5, 0.75, 1].map(fraction =>
        <polygon key={fraction} points={polygon(88 * fraction)} className="comparison-radar-grid" />)}
      {metrics.map((metric, index) => {
        const [x, y] = point(index, 88);
        const [labelX, labelY] = point(index, 111);
        const lines = labelLines(usable.find(axis => axis.key === metric.key).label);
        return <g key={metric.key}>
          <line x1="210" y1="172" x2={x} y2={y} className="comparison-radar-grid" />
          <text x={labelX} y={labelY - (lines.length - 1) * 9}
            textAnchor={labelX < 185 ? 'end' : labelX > 235 ? 'start' : 'middle'}>
            {lines.map((line, lineIndex) => <tspan key={lineIndex} x={labelX} dy={lineIndex === 0 ? 0 : 18}>{line}</tspan>)}
          </text>
        </g>;
      })}
      <text x="218" y="157" className="comparison-radar-tick">0</text>
      <text x="218" y="123" className="comparison-radar-tick">50%</text>
      <text x="218" y="79" className="comparison-radar-tick">100%</text>
      {['a', 'b'].map(series => <g key={series} className={`comparison-radar-series comparison-radar-series-${series}`}>
        <polygon points={metrics.map((metric, index) => point(index, metric[series] * 88).join(',')).join(' ')} />
        {metrics.map((metric, index) => {
          const [x, y] = point(index, metric[series] * 88);
          return series === 'a' ? <circle key={metric.key} cx={x} cy={y} r="4" />
            : <rect key={metric.key} x={x - 3.5} y={y - 3.5} width="7" height="7" />;
        })}
      </g>)}
    </svg>
    <figcaption>{description}
      {metrics.length < axes.length && <span>{incompleteText}</span>}
    </figcaption>
  </figure>;
}
